import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import SubmitButton from '../../../../../components/forms/formComponents/SubmitButton';
import { functions } from '../../../../../firebase-client';
import '../../../teams.css';

function groupTagsByCategory(tags = []) {
  return (Array.isArray(tags) ? tags : []).reduce((acc, tag) => {
    if (!tag || tag.type === 'ageRule') return acc;
    const name = typeof tag?.name === 'string' ? tag.name.trim() : '';
    if (!name) return acc;
    const category = typeof tag?.category === 'string' && tag.category.trim() ? tag.category.trim() : 'Uncategorised';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {});
}

function normaliseMemberTags(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((tag) => {
      if (!tag) return null;
      if (typeof tag === 'string') return { name: tag, category: '' };
      if (typeof tag === 'object' && tag.name) return { name: String(tag.name), category: String(tag.category || '') };
      return null;
    }).filter(Boolean);
  }

  if (typeof raw === 'object') {
    const out = [];
    Object.entries(raw).forEach(([key, value]) => {
      if (value === true || value === 1 || value === 'true') {
        out.push({ name: String(key), category: '' });
        return;
      }
      if (value === false || value == null || value === '') return;
      const values = Array.isArray(value) ? value : [value];
      values.forEach((entry) => {
        if (entry == null || entry === '') return;
        if (typeof entry === 'object' && entry.name) {
          out.push({ name: String(entry.name), category: String(entry.category || key || '') });
          return;
        }
        if (typeof entry === 'string') {
          out.push({ name: String(entry), category: String(key || '') });
        }
      });
    });
    return out;
  }

  return [];
}

function matchesAnyTagFilter(memberTags = [], tagFilters = []) {
  if (!Array.isArray(tagFilters) || tagFilters.length === 0) return false;
  const lowerTags = memberTags.map((tag) => ({
    name: String(tag?.name || '').toLowerCase(),
    category: String(tag?.category || '').toLowerCase(),
  }));

  return tagFilters.some((filter) => {
    const filterName = String(filter?.name || '').toLowerCase();
    const filterCategory = String(filter?.category || '').toLowerCase();
    if (!filterName) return false;
    return lowerTags.some((tag) => {
      if (tag.name !== filterName) return false;
      return !filterCategory || tag.category === filterCategory || tag.category === '';
    });
  });
}

function friendlyError(error) {
  const code = error?.code || error?.message || '';
  if (typeof code === 'string') {
    if (code.includes('permission-denied')) return 'Only admins can send inbox broadcasts.';
    if (code.includes('failed-precondition') && code.includes('No matching members')) return 'No members match that audience.';
    if (code.includes('failed-precondition')) return 'Messaging and Inbox Broadcasts must both be enabled.';
    if (code.includes('invalid-argument')) return 'Check the audience and message, then try again.';
  }
  return 'Could not send the inbox broadcast.';
}

export default function InboxBroadcastComposerModal({
  isOpen,
  onClose,
  teamId,
  tags = [],
  members = [],
  currentUserId = '',
  onSent,
}) {
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState({});
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedMemberIds([]);
    setSelectedTags({});
    setSearch('');
    setMessage('');
    setSending(false);
  }, [isOpen]);

  const groupedTags = useMemo(() => groupTagsByCategory(tags), [tags]);
  const selectedTagFilters = useMemo(() => {
    const out = [];
    Object.entries(selectedTags).forEach(([category, names]) => {
      names.forEach((name) => {
        out.push({ category: category === 'Uncategorised' ? '' : category, name });
      });
    });
    return out.sort((left, right) => left.category.localeCompare(right.category) || left.name.localeCompare(right.name));
  }, [selectedTags]);

  const filteredMembers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const sorted = [...members]
      .filter((member) => member?.id !== currentUserId)
      .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
    if (!needle) return sorted;
    return sorted.filter((member) => String(member?.name || '').toLowerCase().includes(needle));
  }, [currentUserId, members, search]);

  const selectedMemberSet = useMemo(() => new Set(selectedMemberIds), [selectedMemberIds]);
  const resolvedRecipients = useMemo(() => {
    return [...members]
      .filter((member) => member?.id !== currentUserId)
      .filter((member) => selectedMemberSet.has(member.id) || matchesAnyTagFilter(normaliseMemberTags(member?.tags), selectedTagFilters))
      .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
  }, [currentUserId, members, selectedMemberSet, selectedTagFilters]);

  const toggleMember = (memberId) => {
    setSelectedMemberIds((current) => (
      current.includes(memberId)
        ? current.filter((id) => id !== memberId)
        : [...current, memberId]
    ));
  };

  const selectAllFilteredMembers = () => {
    setSelectedMemberIds((current) => {
      const next = new Set(current);
      filteredMembers.forEach((member) => next.add(member.id));
      return Array.from(next);
    });
  };

  const clearFilteredMembers = () => {
    const filteredIds = new Set(filteredMembers.map((member) => member.id));
    setSelectedMemberIds((current) => current.filter((id) => !filteredIds.has(id)));
  };

  const clearAllSelectedMembers = () => {
    setSelectedMemberIds([]);
  };

  const toggleTag = (category, tagName) => {
    setSelectedTags((current) => {
      const existing = new Set(current[category] || []);
      if (existing.has(tagName)) existing.delete(tagName);
      else existing.add(tagName);
      if (existing.size === 0) {
        const rest = { ...current };
        delete rest[category];
        return rest;
      }
      return {
        ...current,
        [category]: Array.from(existing).sort((left, right) => left.localeCompare(right)),
      };
    });
  };

  const handleSend = async () => {
    if (!teamId) return;
    if (!message.trim()) {
      alert('Please enter a message.');
      return;
    }
    if (resolvedRecipients.length === 0) {
      alert('Select at least one member or tag.');
      return;
    }

    try {
      setSending(true);
      const sendInboxBroadcast = httpsCallable(functions, 'sendInboxBroadcast');
      const result = await sendInboxBroadcast({
        teamId,
        memberIds: selectedMemberIds,
        tagFilters: selectedTagFilters,
        message: message.trim(),
      });
      alert(`Inbox broadcast queued for ${result?.data?.queuedRecipientCount || resolvedRecipients.length} recipient${resolvedRecipients.length === 1 ? '' : 's'}.`);
      onSent?.(result?.data || null);
      onClose?.();
    } catch (error) {
      alert(friendlyError(error));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 880 }}>
        <h2 style={{ marginBottom: '0.25rem' }}>New Inbox Message</h2>
        <p className="text-muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Choose members or tag groups. The opening message is sent privately into separate 1:1 threads, where members can reply.
        </p>

        <div className="card" style={{ marginBottom: '0.75rem' }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Audience</h3>
              <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                {selectedMemberIds.length} direct picks • {selectedTagFilters.length} tag filters • {resolvedRecipients.length} resolved recipients
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '0.75rem' }}>
              <div>
                <label>
                  <span className="form-label">Find members</span>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search members by name"
                  />
                </label>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  <button type="button" className="btn btn--secondary btn--xs" onClick={selectAllFilteredMembers}>
                    Select all shown
                  </button>
                  <button type="button" className="btn btn--secondary btn--xs" onClick={clearFilteredMembers} disabled={filteredMembers.length === 0}>
                    Clear shown
                  </button>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={clearAllSelectedMembers} disabled={selectedMemberIds.length === 0}>
                    Clear all
                  </button>
                </div>

                <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: '0.5rem', border: '1px solid #e5e7eb', borderRadius: 12 }}>
                  {filteredMembers.length === 0 ? (
                    <div className="text-muted" style={{ padding: '0.75rem' }}>No members match that search.</div>
                  ) : (
                    filteredMembers.map((member) => (
                      <label
                        key={member.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          padding: '0.65rem 0.75rem',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMemberSet.has(member.id)}
                          onChange={() => toggleMember(member.id)}
                        />
                        <span>{member.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <span className="form-label">Tag groups</span>
                {Object.keys(groupedTags).length === 0 ? (
                  <p className="text-muted" style={{ marginTop: '0.5rem' }}>No team tags available.</p>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem', maxHeight: 300, overflowY: 'auto' }}>
                    {Object.entries(groupedTags).map(([category, list]) => (
                      <div key={category}>
                        <h4 style={{ margin: '0 0 0.35rem' }}>{category}</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {list.map((tag) => {
                            const tagName = typeof tag?.name === 'string' ? tag.name.trim() : '';
                            if (!tagName) return null;
                            const checked = (selectedTags[category] || []).includes(tagName);
                            return (
                              <label
                                key={`${category}:${tagName}`}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.35rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '999px',
                                  padding: '0.3rem 0.6rem',
                                  cursor: 'pointer',
                                  background: checked ? '#eef6ff' : '#fff'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleTag(category, tagName)}
                                />
                                <span>{tagName}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '0.35rem' }}>Resolved recipients</div>
              {resolvedRecipients.length === 0 ? (
                <div className="text-muted">No recipients selected yet.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {resolvedRecipients.slice(0, 18).map((member) => (
                    <span
                      key={member.id}
                      style={{
                        border: '1px solid var(--team-primary)',
                        borderRadius: '999px',
                        padding: '0.15rem 0.5rem',
                        fontSize: '0.85rem'
                      }}
                    >
                      {member.name}
                    </span>
                  ))}
                  {resolvedRecipients.length > 18 && (
                    <span className="text-muted" style={{ fontSize: '0.85rem', alignSelf: 'center' }}>
                      +{resolvedRecipients.length - 18} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <label style={{ display: 'block' }}>
          <span className="form-label">Message</span>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={8}
            placeholder="Write the message every selected member should receive"
            style={{ width: '100%' }}
          />
        </label>

        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <SubmitButton type="button" onClick={onClose} label="Cancel" variant="ghost" disabled={sending} />
          <SubmitButton type="button" onClick={handleSend} label={sending ? 'Sending…' : 'Send Inbox Message'} disabled={sending || resolvedRecipients.length === 0 || !message.trim()} />
        </div>
      </div>
    </div>
  );
}
